import {
  Button,
  type ButtonProps,
  HStack,
  Modal as M,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps as MProps,
} from '@chakra-ui/react';
import { useGlobalState } from '@ui/state/global';
import { useEffect, useRef } from 'react';

type ButtonPropsWithLabel = ButtonProps & {
  label?: string | null;
};

export type ModalProps = {
  title?: string | null;
  children?: React.ReactNode;
  isOpen?: boolean;
  onClose?: (() => void) | null;
  buttons?: ButtonPropsWithLabel[] | null;
  selectionRequired?: boolean | null;
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl';
  initialFocusRef?: MProps['initialFocusRef'];
};

export function Modal({
  isOpen = true,
  onClose,
  title,
  children,
  buttons,
  selectionRequired,
  size,
  initialFocusRef,
}: ModalProps) {
  const lastSizeRef = useRef<ModalProps['size']>(size);
  const { setActiveModal } = useGlobalState();

  // Support for taking getting a callback when the modal is closed
  const handleClose = () => {
    onClose?.();
    setActiveModal(null);
  };

  /**
   * @NOTE(shawk): Without keeping track of the last size, the modal will
   * visibly change size during the exit animation if the size prop changes.
   */
  useEffect(() => {
    lastSizeRef.current = size;
  }, [isOpen, size]);

  return (
    <M
      size={size ?? lastSizeRef.current}
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!selectionRequired}
      closeOnEsc={!selectionRequired}
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay />
      <ModalContent>
        {title && <ModalHeader role="heading">{title}</ModalHeader>}

        {!selectionRequired && <ModalCloseButton title="Close" />}
        {children && <ModalBody>{children}</ModalBody>}

        <ModalFooter>
          <HStack>
            {buttons?.map((button, i) => (
              <Button
                key={`${button.label}-${i}`}
                {...{ borderRadius: '0px' }}
                {...button}
                {...(button.label ? { children: button.label } : {})}
              />
            ))}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </M>
  );
}
